import { Modal, Button, Col, Alert } from 'antd';
import {
  DeleteIconButton,
  EditIconButton,
  FieldRow,
  Form,
  LookupOption,
  TextBox,
  useAppFindFormik,
} from '@/components';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { AddHomeArgs } from '@/redux';
import {
  AssignFloorplanToHomeInForm,
  FormSelectLocation,
  useProjectLookupSource,
} from '@/views';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import {
  AssignFloorplanToHomeDto,
  FloorplanDetailsDto,
  isChooseExistingOption,
} from '@/core';
import Link from 'antd/lib/typography/Link';

const FORM_UID = 'home-details';

const FORBIDDEN_CONSTRUCTION_NUMBER_SYMBOLS = [
  '?',
  '#',
  '/',
  '\\',
  '|',
  '{',
  '}',
  '^',
  '`',
];

const schema = Yup.object().shape({
  homeNumber: Yup.string().field().nullable().required(),
  constructionNumber: Yup.string()
    .field()
    .nullable()
    .forbidSymbols(FORBIDDEN_CONSTRUCTION_NUMBER_SYMBOLS),
  location: Yup.object()
    .shape({
      level1Id: Yup.string().nullable(),
      level2Id: Yup.string().nullable(),
      level3Id: Yup.string().nullable(),
    })
    .nullable(),
  projectId: Yup.string().field().nullable(),
});

interface Props {
  onClose: () => void;
  onSubmit: (props?: any, addAnotherHouse?: boolean) => void;
  value: AddHomeArgs & { id?: string };
  floorplan?: FloorplanDetailsDto;
  preloadInitialFloorplanValue?: LookupOption;
  title: string;
  buttonSaveName: string;
  buttonSaveAndAddName?: string;
  preloadInitialProjectValue?: LookupOption;
  showSaveAndAddButton?: boolean;
  enableReassign?: boolean;
}

interface HomeDetailsModalFooterProps {
  onCancel: () => void;
  saveButtonName: string;
  saveAndAddButtonName?: string;
  showSaveAndAddButton?: boolean;
  onSave: () => void;
  onSaveAndAdd: () => void;
}

function HomeDetailsModalFooter(props: HomeDetailsModalFooterProps) {
  const {
    onCancel,
    saveButtonName,
    saveAndAddButtonName,
    showSaveAndAddButton,
    onSave,
    onSaveAndAdd,
  } = props;
  const { t } = useTranslation();

  return (
    <>
      <Button key="cancel" type="default" onClick={onCancel}>
        {t('homes.details.cancel')}
      </Button>
      {showSaveAndAddButton && (
        <Form.Submit
          key="save-and-add"
          uid={FORM_UID}
          type="primary"
          beforeSubmitted={onSaveAndAdd}
        >
          {saveAndAddButtonName}
        </Form.Submit>
      )}
      <Form.Submit
        key="save"
        uid={FORM_UID}
        type="primary"
        beforeSubmitted={onSave}
      >
        {saveButtonName}
      </Form.Submit>
    </>
  );
}

export function HomeDetailsModal(props: Props) {
  const {
    onClose: onCancel,
    value,
    onSubmit,
    title,
    buttonSaveAndAddName,
    buttonSaveName,
    preloadInitialProjectValue,
    showSaveAndAddButton,
    floorplan,
    preloadInitialFloorplanValue,
    enableReassign,
  } = props;

  const { t } = useTranslation();
  const formik = useAppFindFormik(FORM_UID);

  const addAnotherHouseRef = useRef(false);

  const [showFloorplanPanel, setShowFloorplanPanel] = useState(false);
  const [floorplanName, setFloorplanName] = useState<string>();
  const [floorplanId, setFloorplanId] = useState<string>();

  const projectsDataSource = useProjectLookupSource({
    communityIds: [value.communityId],
    isActive: true,
  });

  const usedLocationIds = useMemo(
    () =>
      [
        value.location?.level1Id,
        value.location?.level2Id,
        value.location?.level3Id,
      ].filter((x): x is string => !!x),
    [value.location],
  );

  const clearFloorplanData = useCallback(() => {
    formik.current?.setFieldValue('floorplanId', undefined);
    formik.current?.setFieldValue('floorplanData', undefined);

    setFloorplanName(floorplan?.marketingName);
    setFloorplanId(floorplan?.id);
    setShowFloorplanPanel(false);
  }, [floorplan, formik]);

  const handleSubmit = useCallback(
    (formValue: AddHomeArgs) => {
      const submitValue =
        formValue.location?.level1Id == null
          ? { ...formValue, location: null! }
          : formValue;

      onSubmit(submitValue, addAnotherHouseRef.current);

      formik.current?.resetForm();
      clearFloorplanData();
    },
    [clearFloorplanData, formik, onSubmit],
  );

  const setFloorplanData = useCallback(
    (floorplanData: AssignFloorplanToHomeDto) => {
      const isChooseExisting = isChooseExistingOption(
        floorplanData.creationOption,
      );

      if (isChooseExisting) {
        formik.current?.setFieldValue('floorplanId', floorplanData.floorplanId);
      } else {
        formik.current?.setFieldValue('floorplanData', floorplanData);
      }

      setShowFloorplanPanel(false);
      setFloorplanName(floorplanData.marketingName);
      setFloorplanId(isChooseExisting ? floorplanData.floorplanId : undefined);
    },
    [formik],
  );

  const handleSave = useCallback(() => {
    addAnotherHouseRef.current = false;
  }, []);

  const handleSaveAndAdd = useCallback(() => {
    addAnotherHouseRef.current = true;
  }, []);

  const handleReassignFloorplan = useCallback(() => {
    setFloorplanName(undefined);
    setFloorplanId(undefined);
    setShowFloorplanPanel(true);
  }, []);

  useEffect(() => {
    setFloorplanName(floorplan?.marketingName);
    setFloorplanId(floorplan?.id);
  }, [floorplan]);

  return (
    <Modal
      title={title}
      visible
      onCancel={onCancel}
      footer={
        <HomeDetailsModalFooter
          onCancel={onCancel}
          saveButtonName={buttonSaveName}
          saveAndAddButtonName={buttonSaveAndAddName}
          showSaveAndAddButton={showSaveAndAddButton}
          onSave={handleSave}
          onSaveAndAdd={handleSaveAndAdd}
        />
      }
    >
      <Form.Formik<AddHomeArgs>
        uid={FORM_UID}
        i18n="homes.details"
        initialValues={value}
        validationSchema={schema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
        validateOnBlur
      >
        <FieldRow>
          <Col span={12}>
            <Form.Input
              name="homeNumber"
              placeholder={t('homes.details.placeholder.homeNumber')}
              required
            />
          </Col>

          <Col span={12}>
            <Form.Input name="constructionNumber" />
          </Col>
        </FieldRow>

        <FieldRow>
          <Col span={12}>
            <FormSelectLocation
              name="location"
              communityId={value.communityId}
              includeUnlistedIds={usedLocationIds}
            />
          </Col>
        </FieldRow>

        <FieldRow>
          <Col span={12}>
            <Form.AsyncSelect
              name="projectId"
              dataSource={projectsDataSource}
              preloadInitial={preloadInitialProjectValue}
              className="w-100"
            />
          </Col>
        </FieldRow>

        <FieldRow>
          {!showFloorplanPanel && !floorplanName && showSaveAndAddButton && (
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setShowFloorplanPanel(true)}
              className="mb-1"
            >
              {t('homes.details.addFloorplan')}
            </Button>
          )}

          {floorplanName && (
            <>
              <Col span={12}>
                <TextBox label={t('homes.details.floorplan')}>
                  <Link
                    target="blank"
                    href={
                      floorplanId
                        ? `/communities/${value.communityId}/floorplans/${floorplanId}`
                        : undefined
                    }
                    disabled={!floorplanId}
                  >
                    {floorplanName}
                  </Link>
                </TextBox>
              </Col>

              <Col span={3}>
                {floorplan && enableReassign && (
                  <EditIconButton
                    title={t('floorplans.details.reassign')}
                    onClick={handleReassignFloorplan}
                  />
                )}

                {!floorplan && (
                  <DeleteIconButton onClick={clearFloorplanData} />
                )}
              </Col>
            </>
          )}
        </FieldRow>

        {showFloorplanPanel && (
          <AssignFloorplanToHomeInForm
            communityId={value.communityId}
            homeId={value.id}
            onCancel={clearFloorplanData}
            onSubmit={setFloorplanData}
            floorplan={floorplan}
            preloadInitialFloorplanValue={preloadInitialFloorplanValue}
          />
        )}

        {value.projectId && (
          <Alert
            className="mt-2"
            description={t('homes.details.changeProjectTip')}
          />
        )}
      </Form.Formik>
    </Modal>
  );
}
